import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { getDevConf, getDevConfList } from '@/api/basic/displaySetting';
import { getCookie } from '@/utils/cookie';
import { Judge } from '@/ConstEnums';
import { checkAndSetUserRoleCookie } from '@/utils/cookie';

// 配置项类型定义
interface ConfigItem {
  conf_key: string;
  conf_value: string;
}

// 配置键名常量，避免硬编码
export const CONFIG_KEYS = {
  WEAK_NETWORK_SWITCH: 'weak_network_switch',
  WEAK_NETWORK_SHOW_TIME_AGAIN: 'weak_network_show_time_again',
  WEAK_NETWORK_TIME_THRESHOLD: 'weak_network_time_threshold',
  WEAK_NETWORK_COUNT_THRESHOLD: 'weak_network_count_threshold',
  WEAK_NETWORK_RTT_THRESHOLD: 'weak_network_rtt_threshold',
  WEAK_NETWORK_PING_THRESHOLD: 'weak_network_ping_threshold',
  WEAK_NETWORK_PING_DOMAIN: 'weak_network_ping_domain',
  WEAK_NETWORK_API_WHITELIST: 'weak_network_api_whiteList',
  WEAK_NETWORK_ROUTER_BLACKLIST: 'weak_network_router_blackList',
  PRINT_DANFE_BUTTON_SWITCH: 'print_danfe_button_switch',
  PRINT_DANFE_BUTTON_SWITCH_CHECKING: 'print_danfe_button_switch_checking',
  ENABLE_FULFILL_CHAIN: 'Enable_Fulfill_Chain',
  ENABLE_FULFILL_CHAIN_INBOUND_DIVERSION_PUTAWAY_RULE:
    'Enable_Fulfill_Chain_Inbound_Diversion_Putaway_Rule',
  CELL_HOTLEVEL_MGMT: 'Cell_hotlevel_mgmt',
  ENABLE_DYNAMIC_REPLENISHMENT: 'Enable_Dynamic_Replenishment',
  ZONE_BY_COLOR: 'zone_by_color',
  DASHBOARD_CONFIGURATION_COLOR: 'dashboard_configuration_color',
  INBOUND_PARCEL_COUNTING: 'Inbound_Parcel_Counting',
  BOXING_DEFAULT_STATION: 'boxing_default_station',
  QC_DEFAULT_STATION: 'QC_default_station',
  USE_DEVICE_INBOUND: 'Use_Device_Inbound',
  SCSAWB_IF_B2C_MODEL: 'SCSAWB_If_B2C_Model',
  ENABLE_IA_INVOICE_STATUS: 'enable_ia_invoice_status',
  USE_NEW_PRINTER_SDK: 'use_new_printer_sdk',
  HIDE_MTB_PRINT_AND_RECOMMEND: 'hide_mtb_print_and_recommend',
  IS_QC_NEED_REASSESSMENT: 'is_qc_need_reassessment',
  AUTO_LOGOUT_AFTER_INACTIVITY_TIME: 'auto_logout_after_inactivity_time',
  CHECKING_STAGING_APPLICATION: 'checking_staging_application',
  CYCLE_COUNT_REQUIREMENT_POOL_SWITCH: 'cycle_count_requirement_pool_switch',
  OUTBOUND_OPERATION_LOG_DETAIL_DISPLAY_EXPORT:
    'outbound_operation_log_detail_display_export',
  ALLOW_WAVE_PLAN_RUN_WAVE: 'allow_wave_plan_run_wave',
  USE_DEFAULT_SURPLUS_LOCATION: 'use_default_surplus_location',
  INDIVIDUAL_TARGET_REQUEST_CYCLE: 'individual_target_request_cycle',
  ENABLE_WORK_ZONE: 'enable_work_zone',
  ONE_STAFF_ONE_ID_FEATURE_CHECKOUT: 'one_staff_one_id_feature_checkout',
  CN_MODEL_WHS: 'CN_Model_WHS',
  UPC_DISPLAY_IN_PC_PDA: 'UPC_display_in_PC_PDA',
  NEW_ASN_QUOTA: 'new_asn_quota',
  ENABLE_RACK_TRANSFER_PLAN: 'Enable_Rack_Transfer_Plan',
  ENABLE_SOUND_NOTIFICATION: 'enable_sound_notification',
  TASK_SIZE_TYPE_MAX_LOAD: 'task_size_type_max_load',
  LOCATION_CC_FLOOR_COLUMN_FILTER_ENABLED:
    'location_cc_floor_column_filter_enabled',
} as const;

// 配置映射工具函数
const createConfigMap = (configList: ConfigItem[]): Map<string, string> => {
  const configMap = new Map<string, string>();
  configList.forEach((item) => {
    if (item && item.conf_key) {
      configMap.set(item.conf_key, item.conf_value);
    }
  });
  return configMap;
};

// 安全获取配置值的工具函数
const getConfigValue = (
  configMap: Map<string, string>,
  key: string,
  defaultValue: any = ''
) => {
  return configMap.get(key) || defaultValue;
};

const getConfigNumber = (
  configMap: Map<string, string>,
  key: string,
  defaultValue = 0
) => {
  return Number(getConfigValue(configMap, key, defaultValue));
};

const getConfigBoolean = (
  configMap: Map<string, string>,
  key: string,
  defaultValue = false
) => {
  return Number(getConfigValue(configMap, key, defaultValue)) === 1;
};

@Module
class System extends VuexModule {
  lang = 'en'; // 当前 i18n lang
  staff_name_display_switch = 0;
  weak_network_switch = 0;
  weak_network_show_time_again = 30;
  weak_network_time_threshold = 3;
  weak_network_count_threshold = 3;
  weak_network_rtt_threshold = 300;
  weak_network_ping_threshold = 100;
  weak_network_ping_domain = '';
  weak_network_api_whiteList = '';
  weak_network_router_blackList = '';
  isOnPrintDANFEButton = false;
  isOnCheckingPrintDANFE = false;
  /** 是否开启履约线开关 */
  enableFulfilChain = false;
  /** 是否使用旧的Rule center inbound putaway 页面 */
  EnableFulfillChainInboundDiversionPutawayRule = false;
  /** 是否展示的 Storage Area Setting Zone/Pathway/Cell 页面的Hot Level */
  CellHotLevelMgmt = false;
  /** 是否开启补货开关 */
  enableDynamicReplenishment = false;
  showZoneColor = false;
  /** 是否展示 dashboard configuration 目录 */
  showDashboardConfiguration = false;
  /** 是否开启包裹计数 */
  inboundParcelCountingConfig = false;

  boxingDefaultStation = '';
  qcDefaultStation = '';
  /** 是否展示Store Group Template **/
  ifB2CModel = 0;
  /** 是否开启入库Device流程 */
  UseDeviceInbound = false;
  /** 是否需要QC重评估 */
  isQcNeedReassessment = false;
  enableIaInvoiceStatus = false;
  /** 是否使用新的打印SDK */
  useNewPrinterSdk = false;
  /** 是否隐藏MTB打印和推荐 */
  hideMtbPrintAndRecommend = false;
  autoLogoutThreshold = 0;
  /** Checking Staging 是否可见 */
  checkingStagingVisible = false;
  /** 是否开启盘点需求池模式 */
  ccRequirementPoolSwitch = false;
  /** 是否开启出库新增过滤器导出 */
  outboundOperationLogDetailDisplayExport = false;
  /** 是否允许波次计划运行波次 */
  allowWavePlanRunWave = false;
  /** Live 环境提示白名单 */
  alertUserList: string[] = [];
  useDefaultSurplusLocation = false;
  individualTargetRequestCycle = 0;
  enableWorkZone = false;
  oneStaffOneIdFeatureCheckout = false;
  /* 在PC和PDA端展示UPC */
  upcDisplayInPcPda = false;
  newAsnQuota = false;
  enableRackTransferPlan = false;
  taskSizeTypeMaxLoad = 1200000;
  locationCcFloorColumnFilterEnabled = false;
  enableSoundNotification = false;

  /**
   * 是否是 CN 模型仓库，用于控制 WFM 菜单展示
   */
  CNModelWHS = false;

  @Mutation
  setCurrentLang(data: any = {}): void {
    this.lang = data.lang;
  }
  /* @Action({ commit: 'setCurrentLang' })
  async getCurrentLang(route: any) {
    return route;
  } */

  @Mutation
  setStaffNameDisplaySwitch(data: number): void {
    this.staff_name_display_switch = data;
  }

  @Action({ commit: 'setStaffNameDisplaySwitch' })
  async loadStaffSwitch() {
    const res = await getDevConf({
      conf_key: 'Is_staffname_display',
      whs_id: getCookie('setting_whs_v2') || '',
    });
    return Number(res.data.conf_value) || 0;
  }

  @Mutation
  setDisplaySettings(data: ConfigItem[] = []): void {
    // 将配置数组转换为key-value映射
    const configMap = createConfigMap(data);

    // 使用key-value映射进行赋值，避免数组下标冲突
    this.weak_network_switch = getConfigNumber(
      configMap,
      CONFIG_KEYS.WEAK_NETWORK_SWITCH
    );
    this.weak_network_show_time_again = getConfigNumber(
      configMap,
      CONFIG_KEYS.WEAK_NETWORK_SHOW_TIME_AGAIN
    );
    this.weak_network_time_threshold = getConfigNumber(
      configMap,
      CONFIG_KEYS.WEAK_NETWORK_TIME_THRESHOLD
    );
    this.weak_network_count_threshold = getConfigNumber(
      configMap,
      CONFIG_KEYS.WEAK_NETWORK_COUNT_THRESHOLD
    );
    this.weak_network_rtt_threshold = getConfigNumber(
      configMap,
      CONFIG_KEYS.WEAK_NETWORK_RTT_THRESHOLD
    );
    this.weak_network_ping_threshold = getConfigNumber(
      configMap,
      CONFIG_KEYS.WEAK_NETWORK_PING_THRESHOLD
    );
    this.weak_network_ping_domain = getConfigValue(
      configMap,
      CONFIG_KEYS.WEAK_NETWORK_PING_DOMAIN
    );
    this.weak_network_api_whiteList = getConfigValue(
      configMap,
      CONFIG_KEYS.WEAK_NETWORK_API_WHITELIST
    );
    this.weak_network_router_blackList = getConfigValue(
      configMap,
      CONFIG_KEYS.WEAK_NETWORK_ROUTER_BLACKLIST
    );
    this.isOnPrintDANFEButton = getConfigBoolean(
      configMap,
      CONFIG_KEYS.PRINT_DANFE_BUTTON_SWITCH
    );
    this.isOnCheckingPrintDANFE = getConfigBoolean(
      configMap,
      CONFIG_KEYS.PRINT_DANFE_BUTTON_SWITCH_CHECKING
    );
    this.enableFulfilChain =
      getConfigNumber(configMap, CONFIG_KEYS.ENABLE_FULFILL_CHAIN) ===
      Judge.Truthy;
    this.EnableFulfillChainInboundDiversionPutawayRule =
      getConfigNumber(
        configMap,
        CONFIG_KEYS.ENABLE_FULFILL_CHAIN_INBOUND_DIVERSION_PUTAWAY_RULE
      ) === Judge.Falsy;
    this.CellHotLevelMgmt =
      getConfigNumber(configMap, CONFIG_KEYS.CELL_HOTLEVEL_MGMT) ===
      Judge.Truthy;
    this.enableDynamicReplenishment =
      getConfigNumber(configMap, CONFIG_KEYS.ENABLE_DYNAMIC_REPLENISHMENT) ===
      Judge.Truthy;
    this.showZoneColor = getConfigBoolean(configMap, CONFIG_KEYS.ZONE_BY_COLOR);
    this.showDashboardConfiguration = getConfigBoolean(
      configMap,
      CONFIG_KEYS.DASHBOARD_CONFIGURATION_COLOR
    );
    this.inboundParcelCountingConfig =
      getConfigNumber(configMap, CONFIG_KEYS.INBOUND_PARCEL_COUNTING) !== 0;
    this.boxingDefaultStation = getConfigValue(
      configMap,
      CONFIG_KEYS.BOXING_DEFAULT_STATION
    );
    this.qcDefaultStation = getConfigValue(
      configMap,
      CONFIG_KEYS.QC_DEFAULT_STATION
    );
    this.UseDeviceInbound =
      getConfigNumber(configMap, CONFIG_KEYS.USE_DEVICE_INBOUND) ===
      Judge.Truthy;
    this.ifB2CModel = getConfigNumber(
      configMap,
      CONFIG_KEYS.SCSAWB_IF_B2C_MODEL
    );
    this.enableIaInvoiceStatus = getConfigBoolean(
      configMap,
      CONFIG_KEYS.ENABLE_IA_INVOICE_STATUS
    );
    this.useNewPrinterSdk = getConfigBoolean(
      configMap,
      CONFIG_KEYS.USE_NEW_PRINTER_SDK
    );
    this.hideMtbPrintAndRecommend = getConfigBoolean(
      configMap,
      CONFIG_KEYS.HIDE_MTB_PRINT_AND_RECOMMEND
    );
    this.isQcNeedReassessment = getConfigBoolean(
      configMap,
      CONFIG_KEYS.IS_QC_NEED_REASSESSMENT
    );
    this.autoLogoutThreshold = getConfigNumber(
      configMap,
      CONFIG_KEYS.AUTO_LOGOUT_AFTER_INACTIVITY_TIME
    );
    this.checkingStagingVisible = getConfigBoolean(
      configMap,
      CONFIG_KEYS.CHECKING_STAGING_APPLICATION
    );
    this.ccRequirementPoolSwitch = getConfigBoolean(
      configMap,
      CONFIG_KEYS.CYCLE_COUNT_REQUIREMENT_POOL_SWITCH
    );
    this.outboundOperationLogDetailDisplayExport = getConfigBoolean(
      configMap,
      CONFIG_KEYS.OUTBOUND_OPERATION_LOG_DETAIL_DISPLAY_EXPORT
    );
    this.allowWavePlanRunWave = getConfigBoolean(
      configMap,
      CONFIG_KEYS.ALLOW_WAVE_PLAN_RUN_WAVE
    );
    this.useDefaultSurplusLocation = getConfigBoolean(
      configMap,
      CONFIG_KEYS.USE_DEFAULT_SURPLUS_LOCATION
    );
    this.individualTargetRequestCycle = getConfigNumber(
      configMap,
      CONFIG_KEYS.INDIVIDUAL_TARGET_REQUEST_CYCLE
    );
    this.enableWorkZone = getConfigBoolean(
      configMap,
      CONFIG_KEYS.ENABLE_WORK_ZONE
    );
    this.oneStaffOneIdFeatureCheckout = getConfigBoolean(
      configMap,
      CONFIG_KEYS.ONE_STAFF_ONE_ID_FEATURE_CHECKOUT
    );
    this.locationCcFloorColumnFilterEnabled = getConfigBoolean(
      configMap,
      CONFIG_KEYS.LOCATION_CC_FLOOR_COLUMN_FILTER_ENABLED
    );
    this.upcDisplayInPcPda = getConfigBoolean(
      configMap,
      CONFIG_KEYS.UPC_DISPLAY_IN_PC_PDA
    );

    this.CNModelWHS = getConfigBoolean(configMap, CONFIG_KEYS.CN_MODEL_WHS);
    this.newAsnQuota = getConfigBoolean(configMap, CONFIG_KEYS.NEW_ASN_QUOTA);
    this.enableRackTransferPlan = getConfigBoolean(
      configMap,
      CONFIG_KEYS.ENABLE_RACK_TRANSFER_PLAN
    );
    this.enableSoundNotification = getConfigBoolean(
      configMap,
      CONFIG_KEYS.ENABLE_SOUND_NOTIFICATION
    );
    this.taskSizeTypeMaxLoad = getConfigNumber(
      configMap,
      CONFIG_KEYS.TASK_SIZE_TYPE_MAX_LOAD,
      1200000
    );

    // 若有一个配置关闭，则要关闭此功能
    if (
      !this.weak_network_show_time_again ||
      !this.weak_network_time_threshold ||
      !this.weak_network_count_threshold ||
      !this.weak_network_rtt_threshold ||
      !this.weak_network_ping_threshold ||
      !this.weak_network_ping_domain
    ) {
      this.weak_network_switch = 0;
    }
  }

  @Action({ commit: 'setDisplaySettings' })
  async loadDisplaySettings() {
    const res = await getDevConfList({
      conf_key: Object.values(CONFIG_KEYS),
      whs_id: getCookie('setting_whs_v2') || '',
    });
    return res.data.conf_list || [];
  }

  @Mutation
  setPortalConfig(data: { alert_user_list?: string[] }): void {
    this.alertUserList = data.alert_user_list || [];
  }

  /**
   * 获取门户配置并处理 Live Tip 白名单
   * 🆕 Live 环境红框提示功能
   */
  @Action({ commit: 'setPortalConfig' })
  async loadPortalConfig() {
    try {
      const res = await getDevConf({
        conf_key: 'white_list_alert_user',
        whs_id: getCookie('setting_whs_v2') || '',
      });

      const data = res.data.conf_value;

      // 🆕 检查用户是否在白名单中,如果 Cookie 不存在则设置
      // 需要从 rootState 获取用户邮箱
      const userEmail = (this.context.rootState as any).user?.userInfo?.email;
      const alertUserList = data.split(',');

      if (userEmail && alertUserList) {
        // checkAndSetUserRoleCookie 会先检查 Cookie 是否存在
        // 只有当 Cookie 不存在且用户在白名单中时才会设置,并返回 true
        const needReload = checkAndSetUserRoleCookie(userEmail, alertUserList);
        // 如果需要刷新,设置标记,让 permission.ts 中的 router.afterEach 执行刷新
        if (needReload) {
          const refreshKey = `live_tip_need_refresh_${userEmail}`;
          const hasRefreshed = sessionStorage.getItem(refreshKey);

          if (!hasRefreshed) {
            // 设置标记,表示需要在路由完成后刷新
            sessionStorage.setItem(refreshKey, Date.now().toString());
            // 同时设置一个全局标记,让 router.afterEach 知道需要刷新
            (window as any).__LIVE_TIP_NEED_REFRESH__ = true;
            console.log('[LiveTip] Will refresh page after route completed');
          }
        }
      }

      return data;
    } catch (error) {
      console.error('[LiveTip] Failed to load portal config:', error);
      return { alert_user_list: [] };
    }
  }
}
export default System;
